/**
 * ブランドのデザイン言語 (brands.design_language・docs/features/brand-cut-motion-graphics-plan.md §10.10.4)。
 *
 * - サイトからの提案は brand_site_proposal ジョブ (features/brands/siteProposal.ts・有料) で受ける。旧 `POST /brands/<id>/design-language/extract`
 *   は 2026-10-30 に廃止。**提案は保存しない** (利用者が編集してからブランドの更新で保存する)。
 * - 保存はブランドの更新 (BrandDetailPage の updateBrandMutation) に `design_language` を載せる。
 * - モーション生成の brief の配色・書体・面の様式の正本になる。ブランドカラーも design_language も無いブランドは
 *   backend がロゴから色を推定する (FE は注意だけ出す: brandColorFallbackNotice)。
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import type { Brand } from './types';

export type DesignPaletteRole = 'background' | 'surface' | 'primary' | 'secondary' | 'accent' | 'text';
export type DesignSurfaceStyle = 'flat' | 'neumorphic' | 'glass' | 'outlined' | 'photo';
export type DesignRadius = 'none' | 'sm' | 'md' | 'lg' | 'pill';

export interface DesignFont {
  family: string;
  weight?: number | string;
  latin?: boolean;
}

export interface BrandDesignLanguage {
  sourceUrl?: string;
  capturedAt?: string;
  palette: Array<{ hex: string; role: DesignPaletteRole; usage?: string }>;
  gradients: Array<{ css: string; usage?: string }>;
  /**
   * display / body = サイトから抽出した参考書体 (任意の文字列)。brandFonts = 利用できる書体 (配信済みカタログに限定・
   * brand_font_families の移行先・計画 brand-product-restructure-plan.md §2.2.4)。brandFonts はキーを省略すると
   * backend が保存済みの値を維持し、空配列で明示クリアになる。読み込み・保存整形・再抽出で落とさないこと
   */
  typography: { display?: DesignFont; body?: DesignFont; notes?: string; brandFonts?: string[] };
  surfaces: { style?: DesignSurfaceStyle; radius?: DesignRadius; shadow?: string };
  mood: string[];
  motionHints: string[];
  summary: string;
  /** backend が保存したスクリーンショットのパス (表示には extract の screenshot_url を使う) */
  screenshotPath?: string;
}

export const DESIGN_PALETTE_ROLES: Array<{ value: DesignPaletteRole; label: string }> = [
  { value: 'background', label: '背景' },
  { value: 'surface', label: '面' },
  { value: 'primary', label: 'メイン' },
  { value: 'secondary', label: 'サブ' },
  { value: 'accent', label: 'アクセント' },
  { value: 'text', label: '文字' },
];

export const DESIGN_SURFACE_STYLES: Array<{ value: DesignSurfaceStyle; label: string }> = [
  { value: 'flat', label: 'フラット' },
  { value: 'neumorphic', label: 'ニューモーフィズム' },
  { value: 'glass', label: 'ガラス (半透明)' },
  { value: 'outlined', label: '線で囲む' },
  { value: 'photo', label: '写真主体' },
];

export const DESIGN_RADII: Array<{ value: DesignRadius; label: string }> = [
  { value: 'none', label: '角丸なし' },
  { value: 'sm', label: '小' },
  { value: 'md', label: '中' },
  { value: 'lg', label: '大' },
  { value: 'pill', label: 'カプセル' },
];

/** 上限 (backend の検証と同じ・§10.10.4) */
export const DESIGN_MOOD_MAX = 8;
export const DESIGN_MOTION_HINTS_MAX = 6;
export const DESIGN_SUMMARY_MAX = 400;

export function emptyDesignLanguage(): BrandDesignLanguage {
  return { palette: [], gradients: [], typography: {}, surfaces: {}, mood: [], motionHints: [], summary: '' };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

const ROLE_SET = new Set(DESIGN_PALETTE_ROLES.map((r) => r.value));
const SURFACE_SET = new Set(DESIGN_SURFACE_STYLES.map((s) => s.value));
const RADIUS_SET = new Set(DESIGN_RADII.map((r) => r.value));

function normalizeFont(raw: unknown): DesignFont | undefined {
  if (!isRecord(raw) || typeof raw.family !== 'string' || !raw.family.trim()) return undefined;
  const font: DesignFont = { family: raw.family };
  if (typeof raw.weight === 'number' || typeof raw.weight === 'string') font.weight = raw.weight;
  if (typeof raw.latin === 'boolean') font.latin = raw.latin;
  return font;
}

function strings(raw: unknown, max: number): string[] {
  return Array.isArray(raw) ? raw.filter((s): s is string => typeof s === 'string' && !!s.trim()).slice(0, max) : [];
}

/** API から来た値 (null / 欠け / 型違いを含む) をフォームで扱える形に揃える。無ければ null。 */
export function normalizeDesignLanguage(raw: unknown): BrandDesignLanguage | null {
  if (!isRecord(raw)) return null;
  const typography = isRecord(raw.typography) ? raw.typography : {};
  const surfaces = isRecord(raw.surfaces) ? raw.surfaces : {};
  const dl: BrandDesignLanguage = {
    palette: Array.isArray(raw.palette)
      ? raw.palette.flatMap((p) => (isRecord(p) && typeof p.hex === 'string'
        ? [{
          hex: p.hex,
          role: (ROLE_SET.has(p.role as DesignPaletteRole) ? p.role : 'accent') as DesignPaletteRole,
          ...(typeof p.usage === 'string' && p.usage ? { usage: p.usage } : {}),
        }]
        : []))
      : [],
    gradients: Array.isArray(raw.gradients)
      ? raw.gradients.flatMap((g) => (isRecord(g) && typeof g.css === 'string'
        ? [{ css: g.css, ...(typeof g.usage === 'string' && g.usage ? { usage: g.usage } : {}) }]
        : []))
      : [],
    typography: {
      ...(normalizeFont(typography.display) ? { display: normalizeFont(typography.display) } : {}),
      ...(normalizeFont(typography.body) ? { body: normalizeFont(typography.body) } : {}),
      ...(typeof typography.notes === 'string' && typography.notes ? { notes: typography.notes } : {}),
      ...(Array.isArray(typography.brandFonts) ? { brandFonts: strings(typography.brandFonts, Number.MAX_SAFE_INTEGER) } : {}),
    },
    surfaces: {
      ...(SURFACE_SET.has(surfaces.style as DesignSurfaceStyle) ? { style: surfaces.style as DesignSurfaceStyle } : {}),
      ...(RADIUS_SET.has(surfaces.radius as DesignRadius) ? { radius: surfaces.radius as DesignRadius } : {}),
      ...(typeof surfaces.shadow === 'string' && surfaces.shadow ? { shadow: surfaces.shadow } : {}),
    },
    mood: strings(raw.mood, DESIGN_MOOD_MAX),
    motionHints: strings(raw.motionHints, DESIGN_MOTION_HINTS_MAX),
    summary: typeof raw.summary === 'string' ? raw.summary.slice(0, DESIGN_SUMMARY_MAX) : '',
  };
  if (typeof raw.sourceUrl === 'string' && raw.sourceUrl) dl.sourceUrl = raw.sourceUrl;
  if (typeof raw.capturedAt === 'string' && raw.capturedAt) dl.capturedAt = raw.capturedAt;
  if (typeof raw.screenshotPath === 'string' && raw.screenshotPath) dl.screenshotPath = raw.screenshotPath;
  return dl;
}

/** 保存前の整形: 空の行・空のフォントを落とし、上限で切る。 */
export function cleanDesignLanguageForSave(dl: BrandDesignLanguage): BrandDesignLanguage {
  const font = (f?: DesignFont): DesignFont | undefined => {
    if (!f || !f.family.trim()) return undefined;
    const weight = typeof f.weight === 'string' ? (f.weight.trim() || undefined) : f.weight;
    return { family: f.family.trim(), ...(weight != null ? { weight } : {}), ...(f.latin != null ? { latin: f.latin } : {}) };
  };
  const display = font(dl.typography.display);
  const body = font(dl.typography.body);
  return {
    ...dl,
    palette: dl.palette
      .map((p) => ({ ...p, hex: p.hex.trim(), ...(p.usage?.trim() ? { usage: p.usage.trim() } : { usage: undefined }) }))
      .filter((p) => !!p.hex),
    gradients: dl.gradients
      .map((g) => ({ ...g, css: g.css.trim(), ...(g.usage?.trim() ? { usage: g.usage.trim() } : { usage: undefined }) }))
      .filter((g) => !!g.css),
    typography: {
      ...(display ? { display } : {}),
      ...(body ? { body } : {}),
      ...(dl.typography.notes?.trim() ? { notes: dl.typography.notes.trim() } : {}),
      // 省略 = 維持・空配列 = クリア を backend が区別するので、キーがあればそのまま (重複だけ除く)
      ...(dl.typography.brandFonts ? { brandFonts: Array.from(new Set(dl.typography.brandFonts.map((f) => f.trim()).filter(Boolean))) } : {}),
    },
    mood: dl.mood.map((s) => s.trim()).filter(Boolean).slice(0, DESIGN_MOOD_MAX),
    motionHints: dl.motionHints.map((s) => s.trim()).filter(Boolean).slice(0, DESIGN_MOTION_HINTS_MAX),
    summary: dl.summary.trim().slice(0, DESIGN_SUMMARY_MAX),
  };
}

/**
 * 抽出結果でフォームを置き換えるとき、保存済み (またはフォーム) の brandFonts を引き継ぐ。抽出 (サイトの読み取り) は
 * 利用できる書体を返さないので、置き換えると書体設定 (と同期している brand_font_families) が消えてしまうため。
 */
export function withKeptBrandFonts(extracted: BrandDesignLanguage, current: BrandDesignLanguage | null | undefined): BrandDesignLanguage {
  const kept = current?.typography.brandFonts;
  if (!kept || extracted.typography.brandFonts) return extracted;
  return { ...extracted, typography: { ...extracted.typography, brandFonts: [...kept] } };
}

/** ブランドのメイン / サブ / アクセント色が全て未設定かつデザイン言語も無い (= backend がロゴから色を推定する)。 */
export function isBrandColorUnset(
  brand: Pick<Brand, 'brand_main_color' | 'brand_sub_color' | 'brand_accent_color' | 'design_language'> | null | undefined,
): boolean {
  if (!brand) return false;
  const colors = [brand.brand_main_color, brand.brand_sub_color, brand.brand_accent_color];
  if (colors.some((c) => typeof c === 'string' && c.trim())) return false;
  return !normalizeDesignLanguage(brand.design_language);
}

export const BRAND_COLOR_FALLBACK_NOTICE = 'ブランドカラー未設定: ロゴから推定して作ります (ブランド管理で設定できます)';

/**
 * ブランド詳細 (GET /brands/<id>) を取る。BrandDetailPage / MotionBrandCutButton と同じ queryKey・同じ形 (data.brand)
 * なのでキャッシュを共有する。
 */
export function useBrandDetail(brandId: string | null | undefined, enabled = true) {
  return useQuery<Brand>({
    queryKey: ['brand', brandId],
    queryFn: async () => (await apiClient.get(`/brands/${brandId}`)).data.brand,
    enabled: enabled && !!brandId,
    staleTime: 60 * 1000,
  });
}
